import {
  Box,
  Button,
  CardContent,
  Chip,
  Card as MuiCard,
  Stack,
  styled,
  Typography,
} from '@mui/material'
import { Children, isValidElement, type ReactNode } from 'react'
import { FilterLiveSearch, useListContext, useTranslate } from 'react-admin'
import { type ChoiceFilterDefinition, useActiveChoiceFilters } from './activeFilters'

// Was MUI v4 withStyles. tss-react's withStyles takes the component first and
// wants media queries as top-level keys rather than nested under `root`;
// `styled` needs neither reshaping and says the same thing.
const Card = styled(MuiCard)(({ theme }) => ({
  [theme.breakpoints.up('sm')]: {
    order: -1, // display on the left rather than on the right of the list
    width: '18em',
    marginRight: '1em',
  },
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}))

/** The <ChoiceFilter> children's definitions, for showing what is in effect. */
const definitionsOf = (children: ReactNode): ChoiceFilterDefinition[] =>
  Children.toArray(children)
    .filter(isValidElement)
    .map((child) => child.props as Partial<ChoiceFilterDefinition>)
    .filter((p): p is ChoiceFilterDefinition => !!p.source && Array.isArray(p.choices))

/** "Aktive Filter": each choice in effect as a chip, removed with its ✕. */
const ActiveFilters = ({ definitions }: { definitions: ChoiceFilterDefinition[] }) => {
  const translate = useTranslate()
  const { active, clear } = useActiveChoiceFilters(definitions)
  if (active.length === 0) return null
  return (
    <Box sx={{ mb: 2 }} data-testid='active-filters'>
      <Typography variant='caption' color='text.secondary'>
        {translate('filters.active')}
      </Typography>
      <Stack direction='row' sx={{ flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
        {active.map((a) => (
          <Chip
            key={a.source}
            size='small'
            color='primary'
            label={`${a.label}: ${a.value}`}
            onDelete={() => clear([a.source])}
          />
        ))}
      </Stack>
    </Box>
  )
}

export const FilterSidebar = ({ children, search }: { children?: ReactNode; search?: string }) => (
  <Card>
    <CardContent>
      <ActiveFilters definitions={definitionsOf(children)} />
      {search && <FilterLiveSearch source={search} />}
      {children}
    </CardContent>
  </Card>
)

/**
 * What a list shows when nothing matches: the search term, and the sidebar
 * filters that narrow it, with a button to lift those — the search stays.
 * For a <Datagrid empty={…}>; on a phone, where the sidebar is hidden, it is
 * the only place the filters show.
 */
export const NoResults = ({
  filters,
  search,
}: {
  filters: ChoiceFilterDefinition[]
  search?: string
}) => {
  const translate = useTranslate()
  const { filterValues } = useListContext()
  const { active, clear } = useActiveChoiceFilters(filters)
  const term = search ? filterValues?.[search] : undefined
  return (
    <Box sx={{ p: 2 }} data-testid='no-results'>
      <Typography>
        {term
          ? translate('filters.no_results_search', { search: term })
          : translate('filters.no_results')}
      </Typography>
      {active.length > 0 && (
        <>
          <Typography color='text.secondary' sx={{ mt: 0.5 }}>
            {translate('filters.filtered_by', {
              filters: active.map((a) => `${a.label} „${a.value}“`).join(', '),
            })}
          </Typography>
          <Button variant='outlined' size='small' sx={{ mt: 1 }} onClick={() => clear()}>
            {translate('filters.clear')}
          </Button>
        </>
      )}
    </Box>
  )
}

export default FilterSidebar
